import React from "react";
import { Link } from "react-router-dom";
import { Wrench, ShieldCheck, Instagram, Mail } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";

/**
 * MaintenancePage
 * -----------------------------------------------------------------------------
 * Full-screen takeover shown to the public while the admin has toggled
 * maintenance mode on. Admins with a valid `admin_token` in localStorage
 * bypass this screen via `MaintenanceGate` in App.js.
 *
 * Props:
 *   primary   — headline copy (falls back to a friendly default)
 *   secondary — supporting message
 *   preview   — when true, renders a "Preview" ribbon (admin preview mode)
 */
export const MaintenancePage = ({ primary, secondary, preview = false }) => {
    const title = primary || "We're upgrading the lab — back shortly.";
    const subtitle = secondary || "Thank you for your patience.";

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-[var(--bg-black,#0a0603)]"
            data-testid="maintenance-page"
        >
            <MolecularBg intensity="low" />

            {/* Ambient gold glow */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(60% 45% at 50% 30%, rgba(233,188,11,0.18) 0%, rgba(233,188,11,0) 70%)",
                }}
            />

            {preview && (
                <div
                    className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-[var(--gold)]/15 border border-[var(--gold)]/50 text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--gold)] z-10"
                    data-testid="maintenance-preview-ribbon"
                >
                    Admin Preview · Site is live for admins only
                </div>
            )}

            <div className="relative z-10 container-x max-w-3xl text-center px-6">
                <div
                    className="mx-auto flex items-center justify-center w-20 h-20 rounded-full mb-8 border border-[var(--gold)]/40 bg-[var(--gold)]/10"
                    style={{ boxShadow: "0 0 60px rgba(233,188,11,0.35)" }}
                >
                    <Wrench size={34} className="text-[var(--gold)]" />
                </div>

                <span className="eyebrow block text-[var(--gold)]/80">// Maintenance</span>

                <h1
                    className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter mt-3"
                    data-testid="maintenance-primary"
                >
                    {title}
                </h1>

                <p
                    className="mt-6 text-base sm:text-lg text-[var(--text-secondary)] max-w-xl mx-auto leading-relaxed"
                    data-testid="maintenance-secondary"
                >
                    {subtitle}
                </p>

                {/* Trust strip */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
                    <div className="glass rounded-2xl p-4 flex items-center gap-3 min-w-0">
                        <ShieldCheck size={18} className="text-[var(--gold)] shrink-0" />
                        <div className="text-left min-w-0">
                            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Existing orders
                            </div>
                            <div className="text-sm text-white">Still processing</div>
                        </div>
                    </div>
                    <div className="glass rounded-2xl p-4 flex items-center gap-3 min-w-0">
                        <Mail size={18} className="text-[var(--gold)] shrink-0" />
                        <div className="text-left min-w-0 flex-1">
                            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Support
                            </div>
                            <a
                                href="mailto:support@carbonlabsau.com"
                                className="text-sm text-white hover:text-[var(--gold)] transition-colors break-all"
                                data-testid="maintenance-support-link"
                            >
                                support@carbonlabsau.com
                            </a>
                        </div>
                    </div>
                    <div className="glass rounded-2xl p-4 flex items-center gap-3 min-w-0">
                        <Instagram size={18} className="text-[var(--gold)] shrink-0" />
                        <div className="text-left min-w-0">
                            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Updates
                            </div>
                            <a
                                href="#" onClick={(e)=>e.preventDefault()} data-brand-todo="instagram"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-sm text-white hover:text-[var(--gold)] transition-colors"
                                data-testid="maintenance-instagram-link"
                            >
                                @carbonlabsau
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-10 text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
                    Carbon Labs · Research Peptides
                </div>

                {preview && (
                    <div className="mt-6">
                        <Link
                            to="/admin"
                            className="text-xs font-mono uppercase tracking-widest text-[var(--gold)]/80 hover:text-[var(--gold)] transition-colors"
                            data-testid="maintenance-preview-back"
                        >
                            ← Return to Admin
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MaintenancePage;
